package com.arena_web.data.dto

import kotlinx.serialization.Serializable

/**
 * Slim event payload for list contexts (Arena feed, My Activity). The detail
 * screen pulls [EventDetailDto] when it needs the heavier fields (description,
 * timer, participants, etc.). Keep this DTO in sync with the backend list
 * mapper — fields removed here MUST also be removed server-side so the
 * response actually gets lighter.
 */
@Serializable
data class EventDto(
    val id: String,
    val sportName: String,
    /** Kept for "Other" sport events — UI renders the user-typed name. */
    val customSportName: String? = null,
    val title: String,
    val status: String,
    val format: Int? = null,
    val maxPlayers: Int? = null,
    val cost: String,
    val imageUrl: String? = null,
    val dateTime: String,
    val latitude: String,
    val longitude: String,
    val address: String,
    val city: String? = null,
    val chatId: String? = null,
    val isPro: Boolean = false,
    val participantsCount: Int,
    val teamsCount: Int? = null,
    val teamScores: List<EventTeamScoreDto> = emptyList(),
    /** WIN / LOSS / DRAW when fetched via participant-scoped endpoint
     *  (`/v1/events?participantId=…`). Null on public listings. */
    val result: String? = null,
    /** PENDING / CONFIRMED for the viewer's own participation. Populated only
     *  by viewer-scoped listings (My Activity); null on public listings. */
    val myParticipantStatus: String? = null,
)

@Serializable
data class EventTeamScoreDto(
    val name: String,
    val score: Int,
    val color: String? = null,
    val imageUrl: String? = null
)

@Serializable
data class EventDetailDto(
    val id: String,
    val creatorId: String,
    val sportId: String,
    val sportName: String,
    val customSportName: String? = null,
    val title: String,
    val description: String? = null,
    val status: String,
    val privacy: String,
    val format: Int? = null,
    val teamsCount: Int? = null,
    val maxPlayers: Int? = null,
    val cost: String,
    val imageUrl: String? = null,
    val dateTime: String,
    val endDateTime: String? = null,
    val latitude: String,
    val longitude: String,
    val address: String,
    val city: String? = null,
    val chatId: String? = null,
    val chatName: String? = null,
    val timer: EventTimerDto,
    val extendedStatsEnabled: Boolean,
    val isPro: Boolean = false,
    val teams: List<EventTeamDto>,
    val participants: List<EventParticipantDto>,
    val createdAt: String,
    val updatedAt: String
)

@Serializable
data class EventTeamDto(
    val id: String,
    val eventId: String,
    val teamId: String? = null,
    val name: String,
    val color: String? = null,
    val officialTeamImageUrl: String? = null,
    val kitHomeImageUrl: String? = null,
    val kitAwayImageUrl: String? = null,
    val formation: FormationDto? = null,
    val members: List<TeamMemberDto> = emptyList(),
    val score: Int,
    val createdAt: String,
    val updatedAt: String
)

@Serializable
data class EventParticipantDto(
    val id: String,
    val userId: String,
    val username: String,
    val fullName: String? = null,
    val avatarUrl: String? = null,
    val overallRating: Double = 0.0,
    val position: String? = null,
    val club: String? = null,
    val country: String? = null,
    val cardImageUrl: String? = null,
    val cardAvatarUrl: String? = null,
    val cardAssetsBasePath: String? = null,
    val cardTextColor: String? = null,
    val eventTeamId: String? = null,
    val status: String,
    val isAdmin: Boolean,
    val createdAt: String
)

@Serializable
data class SwapLineupRequest(
    val userIdA: String,
    val userIdB: String,
)

@Serializable
data class SwapEventTeamsRequest(
    val teamIdA: String,
    val teamIdB: String,
)

@Serializable
data class EventsPageDto(
    val events: List<EventDto>,
    val total: Long,
    val page: Int,
    val size: Int
)

@Serializable
data class CreateEventRequest(
    val title: String,
    val sportId: String,
    val customSportName: String? = null,
    val description: String? = null,
    val privacy: String = "PUBLIC",
    val format: Int? = null,
    val teamsCount: Int? = null,
    val maxPlayers: Int? = null,
    val cost: String = "0.00",
    val imageUrl: String? = null,
    val dateTime: String,
    val endDateTime: String? = null,
    val latitude: String,
    val longitude: String,
    val address: String,
    val city: String? = null,
    val chatId: String? = null,
    val isPro: Boolean = false
)

@Serializable
data class UpdateEventRequest(
    val title: String? = null,
    val description: String? = null,
    val sportId: String? = null,
    // "" (blank) means "clear it" (e.g. switched from Other → real sport);
    // null means "leave unchanged"; non-blank means "set to this value".
    val customSportName: String? = null,
    val privacy: String? = null,
    val format: Int? = null,
    val teamsCount: Int? = null,
    val maxPlayers: Int? = null,
    val cost: String? = null,
    val imageUrl: String? = null,
    val dateTime: String? = null,
    val endDateTime: String? = null,
    val latitude: String? = null,
    val longitude: String? = null,
    val address: String? = null,
    val city: String? = null,
    val isPro: Boolean? = null
)

@Serializable
data class UpdateEventStatusRequest(
    val status: String
)

@Serializable
data class AddEventTeamRequest(
    val name: String,
    val color: String? = null
)

@Serializable
data class SetTeamScoreRequest(
    val score: Int
)

@Serializable
data class AssignTeamRequest(
    val eventTeamId: String
)

@Serializable
data class EventSubstitutionDto(
    val id: String,
    val eventId: String,
    val playerOutId: String,
    val playerInId: String,
    val minute: Int,
    val createdAt: String
)

@Serializable
data class MakeSubstitutionRequest(
    val playerOutId: String,
    val playerInId: String,
    val minute: Int
)

@Serializable
data class TeamInvitationDto(
    val id: String,
    val eventId: String,
    val teamId: String,
    val teamName: String,
    val teamImageUrl: String? = null,
    val inviterId: String,
    val status: String,
    val respondedBy: String? = null,
    val createdAt: String,
    val updatedAt: String
)

@Serializable
data class CreateTeamInvitationRequest(val teamId: String)

@Serializable
data class RespondTeamInvitationRequest(val accept: Boolean)

@Serializable
data class SetAdminRequest(val isAdmin: Boolean)

@Serializable
data class ImageUploadResponse(val imageUrl: String)
